import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { Upload, FileText, CheckCircle, AlertTriangle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function ImportPage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f && (f.name.endsWith('.csv') || f.type === 'text/csv')) {
      setFile(f);
      setResult(null);
    } else {
      toast.error('Please select a CSV file');
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/products/import/csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data);
      toast.success(`Imported: ${data.created} created, ${data.updated} updated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    }
    setImporting(false);
  };

  return (
    <motion.div data-testid="import-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="flex items-center gap-4 mb-8">
        <button data-testid="back-from-import" onClick={() => navigate('/admin/products')} className="p-2 rounded-lg hover:bg-[rgba(26,26,26,0.05)] transition-colors">
          <ArrowLeft size={18} strokeWidth={1.5} />
        </button>
        <h1 className="font-serif text-2xl tracking-tight" style={{ color: '#1A1A1A' }}>Import Products</h1>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Upload Area */}
        <div className="p-8 rounded-lg border-2 border-dashed text-center" style={{ borderColor: 'rgba(26,26,26,0.12)', backgroundColor: '#FDFBF7' }}
          onClick={() => fileRef.current?.click()}>
          <input ref={fileRef} type="file" accept=".csv" onChange={handleFileChange} className="hidden" data-testid="csv-file-input" />
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileText size={24} strokeWidth={1.5} style={{ color: '#C4956A' }} />
              <div className="text-left">
                <p className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>{file.name}</p>
                <p className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.4)' }}>{(file.size / 1024).toFixed(1)} KB</p>
              </div>
            </div>
          ) : (
            <>
              <Upload size={32} strokeWidth={1} className="mx-auto mb-3" style={{ color: 'rgba(26,26,26,0.25)' }} />
              <p className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.5)' }}>Click to upload a CSV file</p>
              <p className="font-sans text-xs mt-1" style={{ color: 'rgba(26,26,26,0.3)' }}>Supports Shopify product export format</p>
            </>
          )}
        </div>

        {file && !result && (
          <Button data-testid="start-import" onClick={handleImport} disabled={importing}
            className="w-full font-sans text-sm rounded-none py-3" style={{ backgroundColor: '#E85D3A', color: '#fff' }}>
            {importing ? 'Importing...' : 'Start Import'}
          </Button>
        )}

        {/* Result */}
        {result && (
          <div className="p-6 rounded-lg border" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#FDFBF7' }}>
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle size={18} strokeWidth={1.5} style={{ color: '#16a34a' }} />
              <h3 className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>Import Complete</h3>
            </div>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="p-3 rounded" style={{ backgroundColor: 'rgba(34,197,94,0.06)' }}>
                <p className="font-sans text-2xl font-medium" style={{ color: '#16a34a' }}>{result.created}</p>
                <p className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.5)' }}>Created</p>
              </div>
              <div className="p-3 rounded" style={{ backgroundColor: 'rgba(59,130,246,0.06)' }}>
                <p className="font-sans text-2xl font-medium" style={{ color: '#2563eb' }}>{result.updated}</p>
                <p className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.5)' }}>Updated</p>
              </div>
              <div className="p-3 rounded" style={{ backgroundColor: 'rgba(26,26,26,0.03)' }}>
                <p className="font-sans text-2xl font-medium" style={{ color: '#1A1A1A' }}>{result.total_products}</p>
                <p className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.5)' }}>Total</p>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <AlertTriangle size={14} strokeWidth={1.5} style={{ color: '#ca8a04' }} />
                  <p className="font-sans text-xs font-medium" style={{ color: '#ca8a04' }}>{result.errors.length} warnings</p>
                </div>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {result.errors.map((err, i) => (
                    <p key={i} className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.5)' }}>Row {err.row}: {err.error}</p>
                  ))}
                </div>
              </div>
            )}
            <Button data-testid="view-products-after-import" onClick={() => navigate('/admin/products')} variant="outline"
              className="mt-4 font-sans text-sm rounded-none w-full" style={{ borderColor: 'rgba(26,26,26,0.15)' }}>
              View Products
            </Button>
          </div>
        )}

        {/* Format Guide */}
        <div className="p-6 rounded-lg border" style={{ borderColor: 'rgba(26,26,26,0.06)', backgroundColor: 'rgba(26,26,26,0.01)' }}>
          <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-3" style={{ color: '#C4956A' }}>Shopify CSV Format</h3>
          <p className="font-sans text-sm mb-3" style={{ color: 'rgba(26,26,26,0.6)' }}>
            Import products using the standard Shopify CSV export format. Required columns:
          </p>
          <div className="grid grid-cols-2 gap-1">
            {['Handle', 'Title', 'Body (HTML)', 'Vendor', 'Tags', 'Variant Price', 'Variant SKU', 'Image Src', 'Status', 'Option1 Name', 'Option1 Value', 'SEO Title'].map(col => (
              <p key={col} className="font-mono text-xs py-0.5" style={{ color: 'rgba(26,26,26,0.5)' }}>{col}</p>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
